import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useToast } from './useToast'

interface ApiError {
  response?: {
    status?: number
    data?: {
      error?: string
      message?: string
    }
    headers?: any
  }
  message?: string
  name?: string
}

export function useApiError() {
  const toast = useToast()
  const { t } = useTranslation()
  
  const handleError = useCallback((error: ApiError, context?: string) => {
    const errorCode =
      (error as { response?: { data?: { error?: { code?: string }; code?: string } } })
        ?.response?.data?.error?.code ??
      (error as { response?: { data?: { code?: string } } })?.response?.data?.code ??
      null

    if (errorCode === 'feature_mainnet_only') {
      toast.warning({ title: t('featureMainnetOnly') })
      return { statusCode: 403, errorMessage: 'feature_mainnet_only', title: 'feature_mainnet_only' }
    }

    if (errorCode === 'AUTH_FAILED') {
      toast.warning({ title: t('toast.sessionExpired') })
      return { statusCode: 401, errorMessage: 'AUTH_FAILED', title: 'AUTH_FAILED' }
    }

    const statusCode = error.response?.status
    const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || 'Unknown error occurred'
    
    // Determine error type and message
    let title = 'Error'
    let description = errorMessage
    let duration = 5000
    
    if (error.name === 'AbortError') {
      title = 'Request Timeout'
      description = 'The operation took too long to complete. Please try again.'
      duration = 8000
    } else if (statusCode === 429) {
      title = 'Rate Limited'
      description = 'Too many requests. Please wait a moment and try again.'
      duration = 10000
    } else if (statusCode === 401) {
      title = 'Unauthorized'
      description = 'Your session has expired. Please login again.'
      duration = 8000
    } else if (statusCode === 403) {
      title = 'Access Denied'
      description = 'You do not have permission to perform this action.'
      duration = 6000
    } else if (statusCode === 404) {
      title = 'Not Found'
      description = errorMessage || 'The requested resource was not found.'
      duration = 5000
    } else if (statusCode && statusCode >= 500) {
      title = 'Server Error'
      description = `The server encountered an error: ${errorMessage}`
      duration = 10000
    } else if (statusCode && statusCode >= 400) {
      title = 'Request Error'
      description = errorMessage
      duration = 6000
    } else if (error.message?.includes('Network')) {
      title = 'Network Error'
      description = 'Unable to connect to the server. Please check your connection.'
      duration = 8000
    }
    
    // Add context if provided
    if (context) {
      description = `${context}: ${description}`
    }
    
    // Generate unique ID based on context and status code
    const toastId = context ? `error-${context.toLowerCase().replace(/\s+/g, '-')}-${statusCode || 'unknown'}` : undefined
    
    // Show toast notification using custom hook
    toast.error({
      title,
      description,
      duration,
      isClosable: true,
      id: toastId, // Prevent duplicate errors for the same operation
    })
    
    // Log error details in development
    if (process.env.NODE_ENV === 'development') {
      console.error('API Error Details:', {
        context,
        status: statusCode,
        data: error.response?.data,
        headers: error.response?.headers,
        message: error.message,
        stack: error
      })
    }
    
    return { statusCode, errorMessage, title }
  }, [toast, t])
  
  return { handleError }
}
